import { now, subtract } from '@eturi/date-util'
import { useConstant, useFn } from '@eturi/react'
import { useT } from '@eturi/react-i18next'
import { isEnter } from '@op/react-web'
import cls from 'classnames'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { useField } from 'react-final-form'
import { checkValuePresence } from '../../../util/checkValuePresence'

const BIRTHDAY_DEFAULT = 'N/A'
const BIRTHDAY_FORMAT = 'YYYY-MM-DD'

const parseAgeToBirthday = (age: number) => {
	const date = subtract(now.subtract(age || 0, 'y'), 1, 'd')
	const MM = `${date.getMonth() + 1}`.padStart(2, '0')
	const DD = `${date.getDate()}`.padStart(2, '0')
	return `${date.getFullYear()}-${MM}-${DD}`
}

const parseBirthdayToAge = (birthday: Maybe<string>) =>
	birthday ? new Date().getFullYear() - moment(birthday, BIRTHDAY_FORMAT).year() : 0

export const ChildAgeField = () => {
	const [t] = useT('child_profile.edit.age_labels')

	const { input } = useField<Maybe<string>>('birthday', {
		subscription: { value: true, initial: true },
	})

	const birthdayValue = input.value ?? BIRTHDAY_DEFAULT
	const initialAge = useConstant(() => parseBirthdayToAge(input.initial))

	const handleKeyDown = useFn((ev: React.KeyboardEvent<HTMLSelectElement>) => {
		if (isEnter(ev)) ev.preventDefault()
	})

	const handleChange = useFn((ev: React.ChangeEvent<HTMLSelectElement>) => {
		const next = ev.target.value
		input.onChange(next !== BIRTHDAY_DEFAULT ? next : null)
	})

	const options = useMemo(
		() =>
			Array.from({ length: 19 }, (_, age) => {
				let labelKey: 'default' | 'zero' | 'one' | 'eighteen_plus' = 'default'
				let value =
					age === initialAge ? (input.initial ?? BIRTHDAY_DEFAULT) : parseAgeToBirthday(age)

				if (age === 0) {
					labelKey = 'zero'
					value = BIRTHDAY_DEFAULT
				} else if (age === 1) {
					labelKey = 'one'
				} else if (age === 18) {
					labelKey = 'eighteen_plus'
				}

				return (
					<option key={age} value={value}>
						{t(labelKey, { age })}
					</option>
				)
			}),
		[t, initialAge, input.initial],
	)

	return (
		<div className="field">
			<select
				name={input.name}
				value={birthdayValue}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
				className={cls(
					checkValuePresence(birthdayValue) ? '!text-[#1c1c28]' : '!text-[#9CA3AF]',
					'px-3 py-3 w-full text-left [text-align-last:left] !text-base bg-transparent',
				)}
			>
				{options}
			</select>
		</div>
	)
}
